<HomeNavBar />

<div class="container-fluid px-2 pb-extra-desktop">
  <!-- Header -->
  <div class="mb-4">
    <h4 class="mb-3 text-dark">
      Search results for <strong>"{{ query }}"</strong>
    </h4>

    <!-- Filter Tabs -->
    <div class="nav nav-pills gap-2 flex-nowrap pb-2">
      <button
        v-for="tab in visible_tabs"
        :key="tab.id"
        class="btn rounded-pill py-2 text-nowrap text-xxs"
        :class="activeTab === tab.id ? 'btn-dark' : 'btn-light border-0 shadow-sm text-primary bg-white'"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>
  </div>

  <!-- Loading State -->
  <div v-if="isLoading" class="text-center py-5 my-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Empty State -->
  <div
    v-else-if="!active_tab_has_results"
    class="text-center py-5 my-5 text-muted bg-white rounded shadow-sm"
  >
    <i class="bx bx-search fs-1 mb-2"></i>
    <h4>No results found</h4>
    <p class="mb-0">Try checking your spelling or use different keywords.</p>
  </div>

  <!-- Main Content -->
  <div v-else class="d-flex flex-column gap-4">
    <!-- Products Section -->
    <div
      v-if="client_features.show_marketplace && (activeTab === 'all' || activeTab === 'products') && products_only.length"
      class="card border-0 shadow-sm rounded"
    >
      <div class="card-header bg-white border-0 pt-3 pb-0">
        <h5 class="fw-bold text-dark fs-7">
          Products
          <span class="badge bg-light text-dark ms-1 rounded-pill">
            {{ products_only.length }}
          </span>
        </h5>
      </div>

      <div class="card-body">
        <!-- 🔥 HORIZONTAL SCROLL ROW -->
        <div class="d-flex flex-row flex-nowrap overflow-auto gap-3 pb-2">
          <div
            v-for="product in (activeTab === 'all' ? products_only.slice(0, 6) : products_only)"
            :key="product.id"
            class="flex-shrink-0"
          >
            <div
              class="marketplace-product-card h-100 d-flex flex-column"
              @click="view_product_details(product)"
            >
              <!-- IMAGE -->
              <div
                class="position-relative bg-light rounded-top d-flex align-items-center justify-content-center overflow-hidden ratio ratio-4x3"
              >
                <img
                  v-if="product.images?.length"
                  :src="product.images[0].image_url"
                  class="w-100 h-100 object-fit-cover"
                />

                <div
                  v-else
                  class="d-flex align-items-center justify-content-center bg-light w-100 h-100"
                >
                  <i class="bx bx-box text-secondary fs-1"></i>
                </div>
              </div>

              <!-- BODY -->
              <div class="card-body p-3">
                <h6
                  class="card-title text-dark text-xxs text-truncate fw-bold mb-2"
                >
                  {{ product.name }}
                </h6>

                <div class="d-flex flex-column gap-2 mt-3">
                  <div class="marketplace-card-price-wrap">
                    <span class="marketplace-card-price">
                      R{{ format_price(product.final_price ?? product.price) }}
                    </span>
                  </div>

                  <button
                    class="btn btn-primary btn-sm marketplace-card-cta"
                    @click.stop="view_product_details(product)"
                  >
                    {{ product.product_type?.name === 'service' ? 'View Service'
                    : 'View Product' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- See more -->
        <div
          v-if="activeTab === 'all' && products_only.length > 6"
          class="text-center mt-4"
        >
          <button
            class="btn btn-outline-dark rounded-pill px-4 py-1 fw-medium btn-sm"
            @click="activeTab = 'products'"
          >
            See all {{ products_only.length }} products
          </button>
        </div>
      </div>
    </div>
    <div v-if="client_features.show_marketplace && (activeTab === 'all' || activeTab === 'services') && services_only.length"
      class="card border-0 shadow-sm rounded mt-4"
    >
      <div class="card-header bg-white border-0 pt-3 pb-0">
        <h5 class="fw-bold text-dark fs-7">
          Services
          <span class="badge bg-light text-dark ms-1 rounded-pill">
            {{ services_only.length }}
          </span>
        </h5>
      </div>

      <div class="card-body">
        <div class="d-flex flex-row flex-nowrap overflow-auto gap-3 pb-2">
          <div
            v-for="service in (activeTab === 'all' ? services_only.slice(0, 6) : services_only)"
            :key="service.id"
            class="flex-shrink-0"
          >
            <div
              class="marketplace-product-card h-100 d-flex flex-column"
              @click="view_profile_product(service)"
            >
              <!-- IMAGE -->
              <div
                class="position-relative bg-light rounded-top d-flex align-items-center justify-content-center overflow-hidden ratio ratio-4x3"
              >
                <img
                  v-if="service.images?.length"
                  :src="service.images[0].image_url"
                  class="w-100 h-100 object-fit-cover"
                />

                <div
                  v-else
                  class="d-flex align-items-center justify-content-center bg-light w-100 h-100"
                >
                  <i class="bx bx-briefcase text-secondary fs-1"></i>
                </div>
              </div>

              <!-- BODY -->
              <div class="card-body p-3">
                <h6
                  class="card-title text-dark text-xxs text-truncate fw-bold mb-2"
                >
                  {{ service.name }}
                </h6>

                <div class="d-flex flex-column gap-2 mt-3">
                  <div class="marketplace-card-price-wrap">
                    <span class="marketplace-card-price">
                      R{{ format_price(service.final_price ?? service.price) }}
                    </span>
                  </div>

                  <button
                    class="btn btn-primary btn-sm marketplace-card-cta"
                    @click.stop="view_profile_product(service)"
                  >
                    View Service
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SEE MORE -->
        <div
          v-if="activeTab === 'all' && services_only.length > 6"
          class="text-center mt-4"
        >
          <button
            class="btn btn-outline-dark btn-sm rounded-pill px-4 py-1"
            @click="activeTab = 'services'"
          >
            See all {{ services_only.length }} services
          </button>
        </div>
      </div>
    </div>
    <!-- Reports Section -->
    <div
      v-if="(activeTab === 'all' || activeTab === 'reports') && results.reports?.length"
      class="card border-0 shadow-sm rounded"
    >
      <div class="card-header bg-white border-0 pt-3 pb-0">
        <h5 class="fw-bold text-dark">
          Reports
          <span class="badge bg-light text-dark ms-1 rounded-pill"
            >{{ results.reports.length }}</span
          >
        </h5>
      </div>
      <div class="card-body">
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
          <div
            class="col"
            v-for="report in (activeTab === 'all' ? results.reports.slice(0, 6) : results.reports)"
            :key="report.id"
          >
            <div
              class="card h-100 border hover-card cursor-pointer shadow-sm rounded"
            >
              <div class="card-body d-flex flex-column">
                <!-- HEADER: Flex Start for vertical stacking of Name and Date -->
                <div class="d-flex align-items-center gap-3 mb-3">
                  <!-- Profile Picture -->
                  <!-- Company Logo -->
                  <img
                    v-if="report.base_profile?.company?.base_profile?.profile_files?.[0]?.file?.file_url"
                    :src="report.base_profile.company.base_profile.profile_files[0].file.file_url"
                    width="40"
                    height="40"
                    class="rounded-circle border object-fit-cover flex-shrink-0"
                    alt="Comp"
                  />

                  <!-- User Profile Pic -->
                  <img
                    v-else-if="report.base_profile?.user?.profile?.profile_files?.[0]?.file?.url"
                    :src="report.base_profile.user.profile.profile_files[0].file.url"
                    width="40"
                    height="40"
                    class="rounded-circle border object-fit-cover flex-shrink-0"
                    alt="User"
                  />

                  <!-- Fallback Initials -->
                  <div
                    v-else
                    class="rounded-circle bg-light d-flex align-items-center justify-content-center border flex-shrink-0 p-2"
                  >
                    <span class="fw-bold text-secondary">
                      {{ (report.base_profile?.company?.company_name ||
                      report.base_profile?.user?.first_name || 'U').charAt(0) }}
                    </span>
                  </div>

                  <!-- Name and Date stacked vertically -->
                  <div class="d-flex flex-column overflow-hidden">
                    <h6 class="fw-bold mb-0 text-dark text-xxs text-truncate">
                      {{ report.base_profile?.company?.company_name ||
                      (report.base_profile?.user ?
                      `${report.base_profile.user.first_name}
                      ${report.base_profile.user.last_name}` : 'Unknown User')
                      }}
                    </h6>
                    <small class="text-xxs text-truncate">
                      {{ report.publication_date ?
                      report.publication_date.split(' ').slice(1).join(' ') : ''
                      }}
                    </small>
                  </div>
                </div>

                <p
                  class="card-text text-muted small text-xxs text-truncate mb-3 flex-grow-1"
                >
                  {{ strip_html(report.description) }}
                </p>

                <button
                  class="btn btn-sm btn-dark text-xxs rounded px-3 py-1 shadow w-100 mt-auto"
                  @click="view_report(report)"
                >
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- See more reports button -->
        <div
          v-if="activeTab === 'all' && results.reports?.length > 6"
          class="text-center mt-4"
        >
          <button
            class="btn btn-outline-dark rounded-pill text-xxs px-4 py-1 fw-medium btn-sm"
            @click="activeTab = 'reports'"
          >
            See all {{ results.reports.length }} reports
          </button>
        </div>
      </div>
    </div>

    <!-- Businesses Section -->
    <div
      v-if="(activeTab === 'all' || activeTab === 'businesses') && results.companies?.length"
      class="card border-0 shadow-sm rounded"
    >
      <div class="card-header bg-white border-0 pt-3 pb-0">
        <h5 class="fw-bold text-dark">
          Businesses
          <span class="badge bg-light text-dark ms-1 rounded-pill"
            >{{ results.companies.length }}</span
          >
        </h5>
      </div>
      <div class="card-body">
        <div
          class="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-4 g-3"
        >
          <div
            class="col"
            v-for="company in (activeTab === 'all' ? results.companies.slice(0, 6) : results.companies)"
            :key="company.id"
          >
            <div
              class="card h-100 border hover-card cursor-pointer shadow-sm rounded"
            >
              <div class="card-body d-flex flex-column">
                <!-- Logo + Name Header -->
                <div class="d-flex align-items-start mb-2">
                  <!-- Logo -->
                  <img
                    v-if="company.base_profile?.profile_files?.[0]?.file?.file_url"
                    :src="company.base_profile.profile_files[0].file.file_url"
                    class="profile-avatar-trigger rounded-circle border object-fit-cover flex-shrink-0 me-2"
                    :alt="company.company_name"
                  />
                  <div
                    v-else
                    class="profile-avatar-trigger rounded-circle border me-2 flex-shrink-0 d-flex align-items-center justify-content-center bg-light text-secondary fw-bold p-2"
                  >
                    <span
                      >{{ company.company_name?.charAt(0).toUpperCase() || 'B'
                      }}</span
                    >
                  </div>

                  <!-- Name & Ratings -->
                  <div class="overflow-hidden min-w-0">
                    <h6 class="mb-1 name-truncate fw-bold text-dark text-xxs">
                      {{ company.company_name }}
                    </h6>

                    <!-- Stars Loop (Fixed: Switched to Boxicons and removed Math.floor) -->
                    <div class="d-flex align-items-center text-xxs">
                      <i
                        v-for="star in 5"
                        :key="star"
                        class="bi me-1"
                        :class="{
                                        'bi-star-fill text-warning': star <= Math.floor(company.rating || 0),
                                        'bi-star-half text-warning': star > Math.floor(company.rating || 0) && star - (company.rating || 0) < 1,
                                        'bi-star text-muted': star > (company.rating || 0)
                                    }"
                      ></i>
                      <span class="ms-1 text-xxs">
                        <!-- Ensure your API returns 'total_ratings' or change this variable -->
                        ({{ company.totalRatings || 0 }})
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Location -->
                <p class="mb-3 text-xxs">
                  <i class="bi bi-geo-alt me-1"></i>
                  {{ company.location || 'Location not specified' }}
                </p>
                <!-- Buttons -->
                <div class="d-flex gap-2 d-flex justify-content-between">
                  <button
                    class="btn btn-primary btn-sm text-xxs rounded"
                    @click="view_profile(company)"
                  >View
                  </button>
                  <button
                    v-if="String(company.base_profile?.creater?.id) !== String(current_user_id)"
                    class="btn btn-primary btn-sm text-xxs rounded"
                    :class="company.is_following ? 'btn-outline-dark' : 'btn-dark'"
                    @click.stop="follow_profile(company)"
                  >
                    <i
                      class="bx me-1"
                      :class="company.is_following ? 'bx-check' : 'bx-plus'"
                    ></i>
                    {{ company.is_following ? 'Following' : 'Follow' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- See more businesses button -->
        <div
          v-if="activeTab === 'all' && results.companies?.length > 6"
          class="text-center mt-4"
        >
          <button
            class="btn btn-outline-dark rounded-pill px-4 py-1 fw-medium btn-sm"
            @click="activeTab = 'businesses'"
          >
            See all {{ results.companies.length }} businesses
          </button>
        </div>
      </div>
    </div>

    <!-- Personal Profiles Section -->
    <div
      v-if="(activeTab === 'all' || activeTab === 'profiles') && results.profiles?.length"
      class="card border-0 shadow-sm rounded"
    >
      <div class="card-header bg-white border-0 pt-3 pb-0">
        <h5 class="fw-bold text-dark">
          People
          <span class="badge bg-light text-dark ms-1 rounded-pill"
            >{{ results.profiles.length }}</span
          >
        </h5>
      </div>
      <div class="card-body">
        <div
          class="row row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 g-3"
        >
          <div
            class="col"
            v-for="profile in (activeTab === 'all' ? results.profiles.slice(0, 6) : results.profiles)"
            :key="profile.id"
          >
            <div
              class="card h-100 border hover-card cursor-pointer shadow-sm rounded"
            >
              <div class="card-body d-flex flex-column">
                <!-- Header: Avatar + Name + Email -->
                <div class="d-flex align-items-center mb-2 px-1">
                  <!-- Avatar -->
                  <!-- 1. Check direct profile_picture on creater -->
                  <img
                    v-if="profile.profile_files?.[0]?.file?.file_url"
                    :src="profile.profile_files?.[0]?.file?.file_url"
                    class="profile-avatar-trigger rounded-circle border object-fit-cover flex-shrink-0 me-3"
                    :alt="profile.user.first_name"
                  />
                  <!-- 3. Fallback to Initials -->
                  <div
                    v-else
                    class="profile-avatar-trigger rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center border flex-shrink-0 me-3 px-3 py-2" 
                  >
                    <span class="fw-bold fs-5">
                      {{ profile.user?.first_name?.charAt(0).toUpperCase() }}{{
                      profile.user?.last_name?.charAt(0).toUpperCase() || ''
                      }}
                    </span>
                  </div>

                  <!-- Name & Email Stacked -->
                  <div class="overflow-hidden">
                    <h6 class="mb-1 name-truncate fw-bold text-dark text-xxs">
                      {{ profile.user?.first_name }} {{ profile.user?.last_name }}
                    </h6>
                  </div>
                </div>

                <!-- Buttons -->
                <div class="d-flex gap-2 d-flex justify-content-between">
                  <button
                    class="btn btn-primary btn-sm text-xxs rounded"
                    @click="view_profile(profile)"
                  > View
                  </button>
                  <button
                    v-if="String(profile.base_profile?.creater?.id) !== String(current_user_id)"
                    class="btn btn-primary btn-sm text-xxs rounded"
                    :class="profile.is_following ? 'btn-outline-dark' : 'btn-dark'"
                    @click.stop="follow_profile(profile)"
                  >
                    <i
                      class="bx me-1"
                      :class="profile.is_following ? 'bx-check' : 'bx-plus'"
                    ></i>
                    {{ profile.is_following ? 'Following' : 'Follow' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- See more people button -->
        <div
          v-if="activeTab === 'all' && results.profiles?.length > 6"
          class="text-center mt-4"
        >
          <button
            class="btn btn-outline-dark rounded-pill px-4 py-1 fw-medium btn-sm"
            @click="activeTab = 'profiles'"
          >
            See all {{ results.profiles.length }} people
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
